import axios from 'axios';
import moment from 'moment';
import { API_URL, ID_SITE, REACT_APP_API_URL_ORG } from '../../conf/env';
import { getUser, setRefreshToken } from '@shared/helpers/auth';
import { gs } from '@shared/helpers/sgn-min';
import {
  onUserLogout,
  refreshToken as refreshTokenAction,
} from '@shared/redux/actions';
import store from '@shared/redux/store';
import packageJson from '../../../../package.json';

window.restartRDE = d => {
  window.localStorage.setItem('rde', '1');
  window.localStorage.setItem(
    'rde_exp',
    moment()
      .add(d ?? 7, 'days')
      .valueOf()
  );
  window.location.reload();
};

const getURL = () => {
  if (window.localStorage.getItem('rde') === '1') {
    let exp = window.localStorage.getItem('rde_exp');
    if (exp) {
      if (moment().isBefore(moment(Number(exp)))) {
        return REACT_APP_API_URL_ORG;
      } else {
        window.localStorage.removeItem('rde');
        window.localStorage.removeItem('rde_exp');
        return API_URL;
      }
    } else {
      window.localStorage.setItem('rde_exp', moment().add(7, 'days').valueOf());
      return REACT_APP_API_URL_ORG;
    }
  } else {
    return API_URL;
  }
};

const http = axios.create({ baseURL: getURL() });

// ==============================
// Utils
// ==============================

function isHotelSearchRequest(url = '') {
  return (
    url.startsWith('/rules/hotels/search-lodging') ||
    url.startsWith('/rules/hotels/fast-search')
  );
}

function getHotelSearchType(url = '') {
  if (url.startsWith('/rules/hotels/search-lodging')) return 'normal';
  if (url.startsWith('/rules/hotels/fast-search')) return 'fast';
  return null;
}

const getCurrentDateFormatted = () => moment().format('YYYYMMDD');

function setVersionHeader() {
  let version = window.localStorage.getItem('app_version');
  if (!version) {
    version = getCurrentDateFormatted();
    window.localStorage.setItem('app_version', version);
  }
}
setVersionHeader();

function getUrl(config) {
  let result;
  if (config?.baseURL) result = config.url?.replace(config.baseURL, '');
  else result = config?.url;
  if (typeof result === 'string' && !result.startsWith('/'))
    result = '/' + result;
  return result;
}

function decodeToken(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

function isTokenExpiring(token, bufferSeconds = 30) {
  const payload = decodeToken(token);
  if (!payload?.exp) return false;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp - now < bufferSeconds;
}

function logTokenExpiration(token) {
  const payload = decodeToken(token);
  if (!payload?.exp) return;
}

// ==============================
// Refresh control (ANTI-RACE)
// ==============================
http.refreshPromise = null;

async function performRefresh() {
  const user = getUser();
  if (!user?.refresh_token) {
    console.warn('[AUTH] no refresh token, skipping refresh');
    return null;
  }

  const cleanAxios = axios.create({ baseURL: API_URL });

  const url = `/users/users/refresh-token/${user.refresh_token}`;
  const method = 'GET';
  const signHeaders = gs(url, method, undefined);

  const response = await cleanAxios
    .get(url, {
      headers: {
        'x-site-id': ID_SITE,
        ...signHeaders.headers,
      },
    })
    .then(res => res.data);

  if (!response?.access_token) {
    throw new Error('Invalid refresh response');
  }

  setRefreshToken(response);
  store.dispatch(refreshTokenAction(response));

  return response.access_token;
}

export async function getValidAccessToken() {
  const user = getUser();
  if (!user?.access_token) return null;

  if (isTokenExpiring(user.access_token)) {
    if (!http.refreshPromise) {
      http.refreshPromise = performRefresh()
        .catch(err => {
          console.error('[AUTH] refresh FAILED');
          const user = getUser();
          if (user?.refresh_token) {
            store.dispatch(onUserLogout());
          }
          throw err;
        })
        .finally(() => {
          http.refreshPromise = null;
        });
    }

    return await http.refreshPromise;
  }

  return user.access_token;
}

// ==============================
// Request interceptor
// ==============================

http.interceptors.request.use(
  async config => {
    const version = window.localStorage.getItem('app_version');
    config.headers = config.headers || {};

    config.headers['x-date'] = moment().format('YYYY-MM-DD');
    config.headers['x-site-id'] = ID_SITE;
    config.headers['x-app-version'] = version;
    config.headers['x-web-version'] = packageJson.version;

    const url = getUrl(config);

    const isHotelSearch = isHotelSearchRequest(url);
    const isDetailsTabs = /\/rules\/hotels\/[^/]+\/detailstabs/.test(url);

    const storedSearchFlowId = sessionStorage.getItem('search-flow-id');

    if (isHotelSearch) {
      delete config.headers['x-flow-id'];
      delete config.headers['x-search-flow-id'];
    } else if (isDetailsTabs) {
      config.headers['x-search-flow-id'] = storedSearchFlowId;
      delete config.headers['x-flow-id'];
    } else {
      const activeFlowId = sessionStorage.getItem('active-flow-id');
      if (activeFlowId) {
        config.headers['x-flow-id'] = activeFlowId;
      }
    }

    console.log(
      `%c ${config.method?.toUpperCase()} - ${url}:`,
      'color: #0086b3; font-weight: bold',
      config
    );

    if (!config.headers.Authorization) {
      const validToken = await getValidAccessToken();
      if (validToken) {
        config.headers.Authorization = `Bearer ${validToken}`;
        logTokenExpiration(validToken);
      }
    }

    // Apply sign-min signature
    const method = config.method?.toUpperCase() || 'GET';
    const body = config.data ? config.data : {};
    const signHeaders = gs(url, method, body);
    config.headers = { ...config.headers, ...signHeaders.headers };

    return config;
  },
  error => {
    throw error;
  }
);

// ==============================
// Response interceptor
// ==============================
async function handleExpectedError(error) {
  console.log(
    `%c ${error.response?.status} - ${getUrl(error.response?.config)}:`,
    'color: #a71d5d; font-weight: bold',
    error.response
  );

  const originalConfig = error.config;

  // No reintentar si no es 401 o ya retry o es login
  if (
    error?.response?.status !== 401 ||
    originalConfig._retry ||
    originalConfig.url === 'users/users/sign-in'
  ) {
    throw error;
  }

  originalConfig._retry = true;

  if (!http.refreshPromise) {
    http.refreshPromise = performRefresh()
      .catch(err => {
        console.error('[AUTH] refresh FAILED');
        const user = getUser();
        if (user?.refresh_token) {
          store.dispatch(onUserLogout());
        }
        throw err;
      })
      .finally(() => {
        http.refreshPromise = null;
      });
  }

  const newToken = await http.refreshPromise;
  if (newToken) {
    originalConfig.headers.Authorization = `Bearer ${newToken}`;
    return http(originalConfig);
  } else {
    throw error;
  }
}

const reloadPaths = [
  '/flights/airports',
  '/hotels/hotels/reference-options',
  '/rules/assist-card/destinations',
];

http.interceptors.response.use(
  res => {
    const url = getUrl(res.config);

    console.log(
      `%c ${res.status} - ${url}:`,
      'color: #008000; font-weight: bold',
      res
    );

    const hotelSearchType = getHotelSearchType(url);

    const flowIdFromHeader = res.headers['x-flow-id'];

    // 🔵 FLOW_1 (origen) — se guarda por separado según sea búsqueda normal o rápida,
    // para que el click del usuario (que ya sabe de cuál búsqueda salió el hotel)
    // decida cuál de los dos usar, en vez de quedarse con el que responda último.
    if (hotelSearchType && flowIdFromHeader) {
      sessionStorage.setItem(
        `search-flow-id-${hotelSearchType}`,
        flowIdFromHeader
      );
    }

    // 🟢 FLOW_2 (activo) — nace en el detalle y se persiste en sessionStorage
    // (no en memoria) para sobrevivir a un reload durante validación/cancelación/reserva.
    const isDetailsTabs = /\/rules\/hotels\/[^/]+\/detailstabs/.test(url);

    if (flowIdFromHeader && isDetailsTabs) {
      sessionStorage.setItem('active-flow-id', flowIdFromHeader);
    }

    const isReloadPath = reloadPaths.some(path => url.startsWith(path));
    const version = Number.parseInt(
      window.localStorage.getItem('app_version'),
      10
    );

    if (isReloadPath && res.headers['cvrs']) {
      const responseVersion = Number.parseInt(res.headers['cvrs'], 10);
      if (
        !Number.isNaN(version) &&
        !Number.isNaN(responseVersion) &&
        version < responseVersion
      ) {
        window.localStorage.setItem('app_version', responseVersion);
        const urlObj = new URL(window.location.href);
        urlObj.searchParams.set('t', Date.now());
        window.location.replace(urlObj.toString());
      }
    }

    return res.data;
  },
  async error => {
    if (error?.response?.status === 450 || error?.response?.status === 451) {
      window.alert(
        'La hora de tu dispositivo no está sincronizada. Por favor, corrígela para continuar usando la aplicación.'
      );
      return new Promise(() => {});
    }
    return handleExpectedError(error);
  }
);

export default http;
